/**
 * The message an institution sends its own students (Sep 12 2026).
 *
 * ── WHY THE INSTITUTION SENDS IT, AND NOT P3 ─────────────────────────────
 * The obvious product is a roster upload: the registrar hands P3 a list of
 * student addresses and P3 emails them. P3 cannot do that. A student roster
 * is an education record, and the route by which a vendor may normally hold
 * one requires the vendor to act under the institution's direct control.
 * P3's Data Sharing Addendum deliberately disclaims that status, because
 * disclaiming it is what lets P3 tell a privacy office it is not processing
 * the school's records. The architecture document works this through in
 * section 5.5 and lands on the same answer: P3 writes the message, the
 * institution sends it from its own mail, and P3 never receives an address.
 *
 * It is also the better product. The message arrives from an advisor the
 * student already knows rather than from a company they have never heard of.
 *
 * ── THIS IS AN ARTIFACT, NOT ADVICE ──────────────────────────────────────
 * Three "put it in front of students" advice steps were cut from the claim
 * centre on Sep 9 2026 (Thomas: advice, not a finding). This is not that. It
 * is a finished message with the institution's own link and code already in
 * it, to copy and send. Nothing here tells the reader how to do their job.
 *
 * ── THE LAST PARAGRAPH IS LOAD-BEARING ───────────────────────────────────
 * "It never sees your messages, your email, or what you do in P3 as one person" is not
 * marketing. It is the sentence that makes a student comfortable claiming an
 * institution, and it is true: this dashboard shows counts and distributions,
 * every cell below ten students is withheld, and no export contains a student
 * row. If that ever stops being true, this sentence comes out first.
 */

import { JOIN_ORIGIN } from "~/app/lib/joincode";

/**
 * Where a student with only the code goes. The join host's root sends them to
 * the member app's code entry. Neither app has a code field (Sep 12 2026), so
 * the message never tells a student to type the code into the app.
 */
const JOIN_HOST = JOIN_ORIGIN.replace(/^https:\/\//, "");

/** What the builder needs. A subset of `Institution`, so a caller cannot pass
 *  a student anything. */
export interface InviteSubject {
  readonly displayName: string;
  readonly joinUrl: string;
  readonly joinCode: string;
}

export interface InviteMessage {
  readonly subject: string;
  readonly body: string;
}

/**
 * The invitation, ready to send.
 *
 * Plain text on purpose: it is pasted into whatever the institution already
 * uses, which is as likely to be a student information system's notification
 * field as it is to be a mail client, and rich text survives neither.
 *
 * The link and the code both appear, because they fail in different places. A
 * link is useless on a printed handout or read aloud in a lecture, and a
 * university's inbound mail security pre-fetches URLs, which is why the code
 * exists and is not a magic link (see `Institution.joinCode`).
 */
export function inviteMessage(institution: InviteSubject): InviteMessage {
  const { displayName, joinUrl, joinCode } = institution;
  return {
    subject: `${displayName} is on P3: free mentors, scholarships and internships`,
    body: [
      `${displayName} has joined P3, a free app that connects students with mentors, scholarships, internships and jobs.`,
      "",
      `To join ${displayName} on P3:`,
      `1. Open https://${joinUrl} on your phone or computer.`,
      "2. Sign in, or create your free account in the P3 app first.",
      `3. Tap Join ${displayName}.`,
      "",
      `No link? Go to ${JOIN_HOST} and enter the code ${joinCode}.`,
      "",
      `Joining lets ${displayName} see counts of how its students use P3. It sees that you joined, by your first name and last initial, and whether you have a mentor. It never sees your messages, your email, or what you do in P3 as one person, and any figure about what students do that would stand for fewer than ten students is not shown at all. You can leave ${displayName} at any time.`,
    ].join("\n"),
  };
}

/** Subject and body as one block, for a paste into a plain text field. */
export function inviteMessageText(institution: InviteSubject): string {
  const { subject, body } = inviteMessage(institution);
  return `Subject: ${subject}\n\n${body}`;
}
